import { wizardCanConfirm } from '@euler/functions/apdm/campaignWizard';
import { useApdmCampaignWizard } from '@euler/functions/apdm/useApdmCampaignWizard';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import {
  ApdmCampaignKind,
  ApdmCampaignMode,
  ApdmCampaignStatus,
  ApdmCampaignWizardStep,
} from '@euler/model/apdm';
import { ReactNode, useState } from 'react';
import { ScrollView, Text, View } from 'react-native';
import { useTheme } from 'styled-components';
import {
  ReviewAction,
  ReviewChoice,
  ReviewField,
  ReviewSection,
} from '../../imports/ReviewControls';
import { WizardAudience } from './WizardAudience';
import {
  WizardBasics,
  wizardKindOptions,
  wizardModeOptions,
} from './WizardBasics';
import { WizardContent } from './WizardContent';
import { WizardToggle } from './WizardControls';
import { WizardPreview } from './WizardPreview';
import { WizardSchedule } from './WizardSchedule';

const steps = [
  { value: ApdmCampaignWizardStep.Basics, label: '基本信息' },
  { value: ApdmCampaignWizardStep.Audience, label: '人群' },
  { value: ApdmCampaignWizardStep.Content, label: '内容' },
  { value: ApdmCampaignWizardStep.Schedule, label: '时间与策略' },
  { value: 'review' as const, label: '预览与确认' },
];
function WizardFrame({
  inline,
  children,
}: {
  inline: boolean;
  children: ReactNode;
}) {
  const theme = useTheme();
  const contentStyle = {
    padding: 16,
    width: '100%' as const,
    maxWidth: 960,
    alignSelf: 'center' as const,
  };
  return inline ? (
    <View style={contentStyle}>{children}</View>
  ) : (
    <ScrollView
      style={{ flex: 1, backgroundColor: theme.page.background }}
      contentContainerStyle={contentStyle}
    >
      {children}
    </ScrollView>
  );
}
export function ApdmCampaignWizard({
  wizard,
  onImport,
  reviewOnly = false,
}: {
  wizard: ReturnType<typeof useApdmCampaignWizard>;
  onImport: () => void;
  reviewOnly?: boolean;
}) {
  const { state, actions } = wizard;
  const currentStep = reviewOnly ? 'review' : state.step;
  const theme = useTheme();
  const confirm = useConfirm();
  const [kind, setKind] = useState(ApdmCampaignKind.WinBack);
  const [mode, setMode] = useState<ApdmCampaignMode | null>(null);
  const [title, setTitle] = useState('');
  const [localError, setLocalError] = useState(false);
  const execute = (operation: () => Promise<unknown>) => {
    setLocalError(false);
    void operation().catch(() => setLocalError(true));
  };
  const disabled =
    state.busy ||
    !state.authorized ||
    !state.campaign ||
    ![ApdmCampaignStatus.Draft, ApdmCampaignStatus.Scheduled].includes(
      state.campaign.status,
    );
  const confirmDraft = async () => {
    const preview = state.preview;
    if (!preview) return;
    if (
      await confirm({
        title: '确认活动',
        message:
          '确认已核对最新预览、排除原因及发送风险？此操作只确认活动，不会启动发送。',
      })
    )
      await actions.confirm(preview);
  };
  const launch = async () => {
    const campaign = state.campaign;
    if (!campaign) return;
    if (
      await confirm({
        title: '启动活动',
        message: '确认开始按已确认的活动内容、发送窗口和策略执行触达？',
        destructive: true,
      })
    )
      await actions.launch(campaign);
  };
  return (
    <WizardFrame inline={reviewOnly}>
      {!reviewOnly && (
        <ReviewSection
          title="创建／编辑客户触达活动"
          description="逐步保存草稿；AI 起草、采用建议、预览、人工确认及启动均为独立操作。"
        >
          {!state.campaign && (
            <>
              <ReviewField
                label="活动名称（可选，服务器可提供默认名称）"
                value={title}
                disabled={state.busy}
                onChange={setTitle}
              />
              <ReviewChoice
                label="活动类型"
                value={kind}
                options={wizardKindOptions}
                disabled={state.busy}
                onChange={setKind}
              />
              <ReviewChoice
                label="活动模式"
                value={mode ?? 'auto'}
                options={[
                  { value: 'auto', label: '自动（使用服务器默认模式）' },
                  ...wizardModeOptions,
                ]}
                disabled={state.busy}
                onChange={value =>
                  setMode(value === 'auto' ? null : (value as ApdmCampaignMode))
                }
              />
              <ReviewAction
                title="创建草稿"
                loading={state.busy}
                onPress={() => execute(() => actions.create(kind, mode, title))}
              />
            </>
          )}
          {state.campaign && (
            <>
              <Text style={{ color: theme.label.color }}>
                草稿版本 {state.campaign.version} ·{' '}
                {state.dirty.length
                  ? '有未保存修改，不能确认'
                  : '当前修改已保存'}
              </Text>
              <ReviewChoice
                label="编辑步骤"
                options={steps}
                value={currentStep}
                disabled={state.busy}
                onChange={actions.move}
              />
              <ReviewAction
                title="重新读取草稿与权限（会丢弃未保存修改）"
                disabled={state.busy}
                onPress={() => execute(() => actions.load(state.campaign!.id))}
              />
            </>
          )}
          {(state.error || localError) && (
            <Text
              accessibilityRole="alert"
              style={{ color: theme.label.color, marginTop: 12 }}
            >
              {state.error ??
                '请核对字段、已发布来源和当前版本。此操作没有自动确认或启动。'}
            </Text>
          )}
        </ReviewSection>
      )}
      {reviewOnly && (state.error || localError) && (
        <Text accessibilityRole="alert" style={{ color: theme.label.color }}>
          {state.error ?? '操作未完成，请打开活动编辑核对最新版本。'}
        </Text>
      )}
      {state.draft && state.authorized && (
        <ReviewSection
          title={
            steps.find(value => value.value === currentStep)?.label ?? '活动'
          }
        >
          {currentStep === ApdmCampaignWizardStep.Basics && (
            <WizardBasics
              identityFrozen={state.identityFrozen}
              draft={state.draft}
              disabled={disabled}
              onChange={draft =>
                actions.edit(ApdmCampaignWizardStep.Basics, draft)
              }
            />
          )}
          {currentStep === ApdmCampaignWizardStep.Audience && (
            <WizardAudience
              draft={state.draft}
              disabled={disabled}
              onChange={draft =>
                actions.edit(ApdmCampaignWizardStep.Audience, draft)
              }
              wizard={wizard}
              onImport={onImport}
            />
          )}
          {currentStep === ApdmCampaignWizardStep.Content && (
            <WizardContent
              draft={state.draft}
              disabled={disabled}
              onChange={draft =>
                actions.edit(ApdmCampaignWizardStep.Content, draft)
              }
              wizard={wizard}
              execute={execute}
            />
          )}
          {currentStep === ApdmCampaignWizardStep.Schedule && (
            <WizardSchedule
              draft={state.draft}
              disabled={disabled}
              onChange={draft =>
                actions.edit(ApdmCampaignWizardStep.Schedule, draft)
              }
            />
          )}
          {currentStep !== 'review' && (
            <ReviewAction
              title="保存这一步并继续"
              loading={state.busy}
              disabled={disabled}
              onPress={() =>
                execute(async () => {
                  if (
                    state.step !== 'review' &&
                    (await actions.save(state.step))
                  )
                    actions.move(
                      steps[
                        steps.findIndex(value => value.value === currentStep) +
                          1
                      ].value,
                    );
                })
              }
            />
          )}
          {currentStep === 'review' &&
            state.campaign &&
            [ApdmCampaignStatus.Draft, ApdmCampaignStatus.Scheduled].includes(
              state.campaign.status,
            ) && (
              <>
                <ReviewAction
                  title="获取当前已保存版本的实际预览"
                  disabled={disabled || state.dirty.length > 0}
                  loading={state.busy}
                  onPress={() => execute(actions.preview)}
                />
                {state.preview && <WizardPreview preview={state.preview} />}
                {state.campaign.status === ApdmCampaignStatus.Draft && (
                  <>
                    <WizardToggle
                      label="我已核对最新实际消息、排除原因及容量警告"
                      value={state.reviewed}
                      disabled={
                        disabled || !state.preview || state.dirty.length > 0
                      }
                      onChange={actions.reviewed}
                    />
                    <ReviewAction
                      title="人工确认活动（不启动）"
                      disabled={
                        state.busy ||
                        !wizardCanConfirm(
                          state.campaign,
                          state.preview,
                          state.dirty,
                          state.authorized,
                          state.reviewed,
                        )
                      }
                      onPress={() => execute(confirmDraft)}
                    />
                  </>
                )}
              </>
            )}
          {state.campaign?.status === ApdmCampaignStatus.Scheduled && (
            <ReviewAction
              title="单独确认并启动活动"
              disabled={
                state.busy || !state.authorized || state.dirty.length > 0
              }
              onPress={() => execute(launch)}
            />
          )}
          {state.campaign?.status === ApdmCampaignStatus.Reaching && (
            <Text style={{ color: theme.label.color }}>
              活动已启动。可返回活动列表查看进度、暂停或结束。
            </Text>
          )}
        </ReviewSection>
      )}
      {!state.authorized && state.campaign && (
        <Text style={{ color: theme.label.color }}>
          当前账号没有活动管理权限，不能修改、确认或启动。
        </Text>
      )}
    </WizardFrame>
  );
}
